import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranchIcon, PlusIcon, StarIcon, CubeIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { useRepositories, createRepository, type Repository } from '@/api-models/repositories';
import { fetchProviderRepos, type ProviderRepo } from '@/api-models/provider-repos';
import { teamPath, useSpace } from '@/contexts/space-context';
import { SettingsSection } from '@/features/settings/components/SettingsSection';
import { AddRepositoryModal } from '@/features/repositories/components/AddRepositoryModal';
import { ApiError } from '@/utils/fetch.client';
import { Button } from '@/ui/button/Button';
import { EmptyState } from '@/ui/EmptyState';

// Vivid per-repo tile colors mirroring the colorful repo dots in the pencil design.
const REPO_TILE_COLORS = ['#5048E8', '#46B6F0', '#3DD68C', '#9A8FF7', '#F5B83D', '#F2585B'];

function repoTileColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return REPO_TILE_COLORS[Math.abs(hash) % REPO_TILE_COLORS.length]!;
}

function RepositoryCard({ repository, teamId }: { repository: Repository; teamId: string }) {
  return (
    <Link
      to={teamPath(teamId, `/sessions/new?project=${repository.id}`)}
      className="group flex flex-col gap-3 p-4 rounded-xl bg-surface-2 border border-line-subtle no-underline transition-colors hover:bg-surface-3 hover:border-line-strong"
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: repoTileColor(repository.id) }}
        >
          <GitBranchIcon size={15} weight="bold" className="text-white" />
        </span>
        <span className="flex-1 min-w-0 truncate text-sm font-semibold text-fg">{repository.name}</span>
        {repository.is_favorite && <StarIcon size={14} weight="fill" className="text-warning shrink-0" />}
      </div>

      <p className="text-xs leading-snug text-fg-tertiary line-clamp-2 min-h-8">
        {repository.description || 'No description'}
      </p>

      <div className="flex items-center gap-3 text-xs text-fg-tertiary tabular-nums">
        {repository.repo_full_name && <span className="truncate text-fg-secondary">{repository.repo_full_name}</span>}
        <span className="flex-1" />
        <span className="flex items-center gap-1.5 shrink-0" title="Workspaces">
          <CubeIcon size={13} />
          {repository.workspace_count ?? 0}
        </span>
      </div>
    </Link>
  );
}

/**
 * Repository management — the list of every repository connected in the active
 * team plus the "add from a provider" flow. Rendered inside Settings › Source
 * control so it sits next to the provider connections that back it.
 */
export function RepositoriesManager() {
  const { teamId } = useSpace();
  const { repositories, isLoading, mutate } = useRepositories(teamId);

  const [addOpen, setAddOpen] = useState(false);
  const [repos, setRepos] = useState<ProviderRepo[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState<{ status: number; message: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const loadRepos = useCallback(async (query?: string) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setReposLoading(true);
    setReposError(null);
    try {
      setRepos(await fetchProviderRepos(query, ctrl.signal));
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setReposError(
        e instanceof ApiError
          ? { status: e.status, message: e.message }
          : { status: 0, message: 'Failed to load repositories' },
      );
    } finally {
      if (!ctrl.signal.aborted) setReposLoading(false);
    }
  }, []);

  const openAdd = useCallback(() => {
    setAddOpen(true);
    void loadRepos();
  }, [loadRepos]);

  const handleCreate = useCallback(
    async (repo: ProviderRepo) => {
      try {
        await createRepository(teamId, {
          name: repo.name,
          description: repo.description,
          provider: repo.provider,
          provider_repo_id: repo.id,
          repo_full_name: repo.full_name,
          default_branch: repo.default_branch,
          language: repo.language,
        });
        await mutate();
        setAddOpen(false);
        toast.success(`Created "${repo.name}"`);
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Failed to create repository');
      }
    },
    [teamId, mutate],
  );

  return (
    <SettingsSection
      title="Repositories"
      description="Repositories connected from the providers above. Open one to start a workspace."
      action={
        !isLoading && repositories.length > 0 ? (
          <Button
            type="button"
            variant="secondary"
            size={7}
            onClick={openAdd}
            iconLeft={<PlusIcon size={14} weight="bold" />}
          >
            New repository
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-14 text-sm text-fg-tertiary">Loading repositories…</div>
      ) : repositories.length === 0 ? (
        <EmptyState
          icon={CubeIcon}
          title="No repositories yet"
          actions={
            <Button
              type="button"
              variant="primary"
              size={7}
              onClick={openAdd}
              iconLeft={<PlusIcon size={14} weight="bold" />}
            >
              New repository
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {repositories.map((r) => (
            <RepositoryCard key={r.id} repository={r} teamId={teamId} />
          ))}
        </div>
      )}

      <AddRepositoryModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        repos={repos}
        isLoadingRepos={reposLoading}
        onCreateRepository={handleCreate}
        onSearch={loadRepos}
        error={reposError}
        onRetry={() => loadRepos()}
      />
    </SettingsSection>
  );
}
